/**
 * Announcement Bar Component
 * ============================
 * Web Component that auto-rotates through multiple announcement messages
 * with a fade transition. Pauses on hover so users can read the current message.
 *
 * Usage:
 *   <announcement-bar>
 *     <p class="announcement-bar__message is-active">Message 1</p>
 *     <p class="announcement-bar__message">Message 2</p>
 *     <p class="announcement-bar__message">Message 3</p>
 *   </announcement-bar>
 *
 * If only one message exists, no rotation occurs.
 */

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();

      /** Index of the currently visible message */
      this.currentIndex = 0;

      /** Rotation interval ID */
      this.intervalId = null;

      /** Milliseconds between message changes */
      this.rotationSpeed = 5000;
    }

    connectedCallback() {
      this.messages = this.querySelectorAll('.announcement-bar__message');

      /* Nothing to rotate if there is only one (or zero) messages */
      if (this.messages.length <= 1) return;

      /* Make sure exactly the first message is visible on load */
      this.messages.forEach((msg, index) => {
        msg.classList.toggle('is-active', index === 0);
        msg.setAttribute('data-index', index);
      });

      this.setAttribute('data-index', '0');
      this.startRotation();

      /* Pause on hover so the user can read the message */
      this.addEventListener('mouseenter', () => this.pauseRotation());
      this.addEventListener('mouseleave', () => this.startRotation());

      /* Pause on touch for mobile users */
      this.addEventListener('touchstart', () => this.pauseRotation(), { passive: true });
      this.addEventListener('touchend', () => this.startRotation(), { passive: true });
    }

    disconnectedCallback() {
      this.pauseRotation();
    }

    /**
     * Begin (or resume) the auto-rotation timer.
     */
    startRotation() {
      /* Clear any existing interval to avoid duplicates */
      this.pauseRotation();

      this.intervalId = setInterval(() => {
        this.showNext();
      }, this.rotationSpeed);
    }

    /**
     * Stop the auto-rotation timer.
     */
    pauseRotation() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    /**
     * Advance to the next message with a fade transition.
     * The CSS handles the opacity transition (.announcement-bar__message
     * transitions opacity over 0.5s).
     */
    showNext() {
      /* Hide current message */
      this.messages[this.currentIndex].classList.remove('is-active');

      /* Advance index, wrapping around */
      this.currentIndex = (this.currentIndex + 1) % this.messages.length;

      /* Show new message */
      this.messages[this.currentIndex].classList.add('is-active');

      /* Update data-index on the component for external use */
      this.setAttribute('data-index', this.currentIndex);
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
